'use client'

import { useState, useMemo } from 'react'
import Checkbox from '@mui/material/Checkbox'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import { Icon } from '@iconify/react'
import { createColumnHelper } from '@tanstack/react-table'

// Add styles from Enquiry module for UI consistency
import '../../../Enquiries/Enquiries/EnquiriesModule.css';

import PremiumModuleWrapper from '../../../Task/Components/common/PremiumModuleWrapper'
import OptionMenu from '../../../../components/common/Template_Layouts/@core/components/option-menu'
import ScoreCard from '../../../../components/common/ScoreCard/ScoreCard'
import Loader from '../../../../components/Loader/Loader'
import { getInitials, getAvatarColorScheme } from '../../../../utils/helpers'
import { formatDate } from '../../../Task/Components/common/premiumUtils'
import { BsTelephonePlus } from "react-icons/bs";

import { User } from 'lucide-react'
const columnHelper = createColumnHelper()

const LeadListWrapper = ({
    data = [],
    stats,
    loading,
    onViewLead,
    onEditLead,
    onDeleteLead,
    totalRecords,
    currentPage,
    itemsPerPage,
    onPageChange,
    onStatusChange,
    rowSelection = {},
    onManualRowSelectionChange,
    onSelectAll,
    activeFilters = ['All'],
    searchTerm,
    onRowAction,
    onScheduleCall,
    onInitiateCall
}) => {
    const getAvatar = (params, rowData) => {
        const { name } = params
        const scheme = getAvatarColorScheme(name || 'Unknown')

        return (
            <ScoreCard
                leadId={rowData.ID || rowData.LeadId || rowData.Id}
                mobileNo={rowData.MobileNo}
                personName={rowData.PersonName}
            >
                <div className="lead-avatar-circular-container">
                    <div
                        className="avatar-circle"
                        style={{ backgroundColor: scheme.bg, color: scheme.text }}
                    >
                        {getInitials(name || 'Unknown')}
                    </div>

                    {/* Followup Count Badge */}
                    <div className="score-badge-wrapper">
                        <span className="score-badge">{rowData.FollowupCount || 0}</span>
                    </div>

                    {/* Call Icon Overlay */}
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            if (onScheduleCall) onScheduleCall(rowData);
                        }}
                        className="call-overlay-btn"
                        title="Schedule Call"
                    >
                        <BsTelephonePlus size={20} />
                    </button>
                </div>
            </ScoreCard>
        )
    }

    const columns = useMemo(() => [
        {
            id: 'select',
            header: ({ table }) => {
                const isAllPageSelected = data.length > 0 && data.every(row => {
                    const id = String(row.id || row.ID || row.LeadId || row.idNum || row.TaskHistoryId || '');
                    return !!rowSelection[id];
                });

                const isIndeterminate = !isAllPageSelected && data.some(row => {
                    const id = String(row.id || row.ID || row.LeadId || row.idNum || row.TaskHistoryId || '');
                    return !!rowSelection[id];
                });

                return (
                    <Checkbox
                        checked={isAllPageSelected}
                        indeterminate={isIndeterminate}
                        onChange={(e) => {
                            if (isAllPageSelected) {
                                onManualRowSelectionChange({});
                            } else {
                                if (onSelectAll) onSelectAll();
                                else table.toggleAllRowsSelected(true);
                            }
                        }}
                        sx={{
                            color: '#d1d5db',
                            '&.Mui-checked': { color: '#0eb18f' },
                            '&.MuiCheckbox-indeterminate': { color: '#0eb18f' }
                        }}
                    />
                )
            },
            cell: ({ row }) => {
                const id = String(row.original.id || row.original.ID || row.original.LeadId || row.original.idNum || row.original.TaskHistoryId || '');
                return (
                    <Checkbox
                        checked={!!rowSelection[id]}
                        onChange={(e) => {
                            const newSelection = { ...rowSelection };
                            if (e.target.checked) {
                                newSelection[id] = true;
                            } else {
                                delete newSelection[id];
                            }
                            onManualRowSelectionChange(newSelection);
                        }}
                        sx={{
                            color: '#d1d5db',
                            '&.Mui-checked': { color: '#0eb18f' }
                        }}
                    />
                )
            }
        },
        columnHelper.accessor('PersonName', {
            header: 'LEAD DETAILS',
            cell: ({ row }) => (
                <div className="flex items-center gap-3">
                    <div
                        className="relative group/avatar cursor-pointer flex-shrink-0"
                        onClick={(e) => {
                            e.stopPropagation();
                            onViewLead(row.original);
                        }}
                    >
                        <ScoreCard 
                            leadId={row.original.id || row.original.ID || row.original.LeadId || row.original.idNum || row.original.TaskHistoryId}
                            personName={row.original.PersonName} 
                            mobileNo={row.original.MobileNo}
                        >
                            {(() => {
                                const name = row.original.PersonName;
                                const isNoName = !name || name === '-' || name.trim() === '';
                                const avatarStyle = getAvatarColorScheme(name || 'Unknown');
                                return (
                                    <div
                                        className="w-9 h-9 rounded-full flex items-center justify-center text-[13px] font-bold border-2 border-white shadow-sm transition-all group-hover/avatar:scale-105"
                                        style={{
                                            backgroundColor: isNoName ? '#f8fafc' : avatarStyle.bg,
                                            color: isNoName ? '#94a3b8' : avatarStyle.text,
                                            border: `1px solid ${isNoName ? '#e2e8f0' : (avatarStyle.border || '#e2e8f0')}`
                                        }}
                                    >
                                        {isNoName ? (
                                            <User size={18} strokeWidth={2.5} />
                                        ) : (
                                            getInitials(name)
                                        )}
                                    </div>
                                );
                            })()}
                        </ScoreCard>
                        {/* Followup Count Badge */}
                        <div className="score-badge-wrapper">
                            <span className="score-badge">{row.original.FollowupCount || 0}</span>
                        </div>
                    </div>
                    <div className="flex flex-col gap-1">
                        <span 
                            className="ph-name-link text-[14px] font-semibold truncate"
                            onClick={(e) => {
                                e.stopPropagation();
                                onViewLead(row.original);
                            }}
                        >
                            {row.original.PersonName || '-'}
                        </span>
                        <div className="flex items-center gap-2">
                            <span className="text-[11px] font-semibold text-gray-700">Lead No: {row.original.LeadNo}</span>
                        </div>
                    </div>
                </div>
            )
        }),
        columnHelper.accessor('MobileNo', {
            header: 'MOBILE NO',
            cell: ({ row }) => (
                <div className="flex items-center gap-2">
                    <button
                        className="hover:scale-110 transition-transform p-1 rounded-full hover:bg-gray-100"
                        onClick={(e) => {
                            e.stopPropagation();
                            if (onScheduleCall) onScheduleCall(row.original);
                        }}
                    >
                        <Icon icon="material-symbols:add-call-outline" className="text-slate-400 hover:text-blue-600" width={18} />
                    </button>
                    <span 
                        className="ph-name-link truncate font-semibold"
                        onClick={(e) => {
                            if (onInitiateCall) onInitiateCall(row.original, e);
                        }}
                    >
                        {row.original.MobileNo || '-'}
                    </span>
                </div>
            )
        }),
        columnHelper.accessor('EmailID', {
            header: 'EMAIL ID',
            cell: ({ row }) => (
                <span className="text-[13px] text-black font-semibold truncate block max-w-[200px]" title={row.original.EmailID}>
                    {row.original.EmailID || '-'}
                </span>
            )
        }),
        columnHelper.accessor('CreatedOn', {
            header: 'CREATION DETAILS',
            cell: ({ row }) => (
                <div className="flex flex-col gap-1 py-1">
                    <div className="flex items-center gap-2 whitespace-nowrap">
                        <span className="text-[11px] font-bold text-slate-700">Date</span>
                        <span className="ph-created-date !text-[14px] text-[#334155] font-semibold">
                            {row.original.CreatedOn || '-'}
                        </span>
                    </div>
                    <div className="flex items-center gap-2 whitespace-nowrap">
                        <span className="text-[11px] font-bold text-slate-700">Owner</span>
                        <span className="text-[13px] text-black font-semibold">
                            {row.original.AssignedToName || '-'}
                        </span>
                    </div>
                </div>
            )
        }),
        columnHelper.accessor('SourceName', {
            header: 'SOURCE',
            cell: ({ row }) => (
                <span className="px-2 py-0.5 bg-gray-50 text-gray-700 text-[10px] font-bold border border-gray-200 tracking-wider" style={{ borderRadius: '8px' }}>
                    {row.original.SourceName ? row.original.SourceName.toUpperCase() : '-'}
                </span>
            )
        }),
        columnHelper.accessor('FollowupStatus', {
            header: 'STATUS',
            cell: ({ row }) => (
                <span className="text-[12px] font-semibold text-gray-800">{row.original.FollowupStatus || '-'}</span>
            )
        }),
        columnHelper.accessor('LastFollowupDate', {
            header: 'LASTFOLLOWUPED ON',
            cell: ({ row }) => (
                <span className="text-[12px] font-medium text-gray-700">{row.original.LastFollowupDate || '-'}</span>
            )
        }),
        columnHelper.accessor('Remarks', {
            header: 'REMARKS',
            cell: ({ row }) => (
                <div className="flex flex-col">
                    <span className="text-[11px] font-bold text-slate-700">Remarks</span>
                    <span className="text-[12px] text-black font-semibold truncate max-w-[150px]" title={row.original.Remarks}>
                        {row.original.Remarks || 'No remarks'}
                    </span>
                </div>
            )
        }),
        columnHelper.display({
            id: 'actions',
            header: 'ACTIONS',
            cell: ({ row }) => {
                let settingActions = [];
                if (onEditLead) {
                    settingActions.push({
                        text: 'Edit',
                        icon: <Icon icon='ri:edit-line' fontSize={20} style={{ color: '#6366f1' }} />,
                        menuItemProps: { className: 'flex items-center gap-2', onClick: () => onEditLead(row.original) }
                    });
                }
                if (onDeleteLead) {
                    settingActions.push({
                        text: 'Delete',
                        icon: <Icon icon='ri:delete-bin-line' fontSize={20} style={{ color: '#ef4444' }} />,
                        menuItemProps: { className: 'flex items-center gap-2', onClick: () => onDeleteLead(row.original) }
                    });
                }

                return (
                    <div className='flex items-center justify-center gap-2 w-full min-w-[60px]'>
                        {settingActions.length > 0 && (
                            <OptionMenu
                                iconClassName='text-textSecondary'
                                icon={<Icon icon='tabler:settings' fontSize={20} style={{ color: '#64748b' }} />}
                                iconButtonProps={{
                                    sx: {
                                        border: '1px solid #e2e8f0',
                                        borderRadius: '8px',
                                        padding: '7px',
                                        '&:hover': { backgroundColor: '#f1f5f9', borderColor: '#cbd5e1' }
                                    }
                                }}
                                options={settingActions}
                            />
                        )}
                    </div>
                );
            }
        })
    ], [data, rowSelection, onViewLead, onEditLead, onDeleteLead])

    const customCards = [
        {
            title: stats?.total || 0,
            subtitle: 'Total Leads',
            icon: 'ri:user-star-line',
            color: 'primary',
            key: 'All'
        },
        {
            title: stats?.today || 0,
            subtitle: "Today's Created Leads",
            icon: 'ri:calendar-event-line',
            color: 'success',
            key: 'Today'
        }
    ];

    return (
        <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
            <PremiumModuleWrapper
                moduleName="Lead"
                stats={stats}
                onStatusClick={onStatusChange}
                loading={loading}
                hasFetched={true}
                data={data}
                columns={columns}
                totalRecords={totalRecords}
                currentPage={currentPage}
                itemsPerPage={itemsPerPage}
                onPageChange={onPageChange}
                showCards={true}
                customCards={customCards}
                activeFilters={activeFilters}
                globalFilter={searchTerm}
            />
        </Box>
    );
}

export default LeadListWrapper
